import { ErrorTypeEnum } from '../../constants/error-type.ts';

import { getSentryUtil } from '../monitoring/sentry.util.ts';

import { getAudioContextUtil } from './audio-context.util.ts';

const BUFFER_MIME_TYPE = 'audio/mpeg';

export class AudioMediaSourceUtil {
  private sourceBuffer: SourceBuffer | null = null;
  private readonly audio: HTMLAudioElement;
  private readonly mediaSource: MediaSource;
  private readonly queue: Uint8Array[] = [];
  private isPlaying = false;
  private isClosed = false;
  private pendingPlayResolve: (() => void) | null = null;

  private readonly sourceBufferReady: Promise<void>;
  private sourceBufferReadyResolve: (() => void) | null = null;

  public constructor(
    private readonly start: () => Promise<void>,
    private readonly onAudioPlay: (() => void) | undefined,
    private readonly audioContextUtil = getAudioContextUtil(),
    private readonly sentryUtil = getSentryUtil()
  ) {
    this.sourceBufferReady = new Promise((resolve) => {
      this.sourceBufferReadyResolve = resolve;
    });

    this.audio = this.createAudioElement();
    this.mediaSource = this.createMediaSource();
  }

  private createAudioElement(): HTMLAudioElement {
    const audio = document.createElement('audio');
    if (this.onAudioPlay) {
      audio.addEventListener('play', () => this.onAudioPlay?.());
    }

    audio.addEventListener('ended', () => {
      this.pendingPlayResolve?.();
      this.cleanup();
    });

    return audio;
  }

  private createMediaSource(): MediaSource {
    const mediaSource = new MediaSource();
    this.audio.src = URL.createObjectURL(mediaSource);
    mediaSource.addEventListener('sourceopen', () => this.onSourceOpen());
    return mediaSource;
  }

  private async onSourceOpen(): Promise<void> {
    this.sourceBuffer = this.mediaSource.addSourceBuffer(BUFFER_MIME_TYPE);
    this.sourceBuffer.addEventListener('updateend', () => this.onSourceBufferUpdateEnd());
    this.sourceBufferReadyResolve?.();

    await this.start();
  }

  private onSourceBufferUpdateEnd(): void {
    if (!this.sourceBuffer) return;
    if (this.sourceBuffer.updating) return;
    if (this.queue.length === 0) {
      if (this.isClosed) {
        this.closeMediaSourceStream();
      }
      return;
    }

    const buffer = this.queue.shift()!;
    this.sourceBuffer.appendBuffer(buffer);
  }

  public appendBuffer(buffer: Uint8Array): void {
    if (!this.sourceBuffer) return;
    if (this.isClosed) return;
    if (!this.isPlaying || this.sourceBuffer.updating || this.queue.length > 0) {
      this.queue.push(buffer);
      return;
    }

    this.sourceBuffer.appendBuffer(buffer);
    this.audio.play();
  }

  public async play(volume?: number): Promise<void> {
    if (this.isPlaying) return;

    await this.sourceBufferReady;
    if (!this.sourceBuffer) return;

    this.isPlaying = true;
    await this.audioContextUtil.prepareMediaElementSource(this.audio, volume);

    if (this.queue.length > 0) {
      const buffer = this.queue.shift()!;
      this.sourceBuffer.appendBuffer(buffer);
      this.audio.play();
    }

    return new Promise((resolve) => {
      this.pendingPlayResolve = resolve;
    });
  }

  public stop(): void {
    this.isPlaying = false;
    this.audio.pause();
    this.audio.currentTime = 0;
    this.close();
    this.pendingPlayResolve?.();
  }

  public close(): void {
    this.isClosed = true;
    if (this.queue.length === 0) {
      this.closeMediaSourceStream();
    }
  }

  private closeMediaSourceStream(): void {
    if (this.sourceBuffer?.updating) return;
    if (this.mediaSource.readyState !== 'open') return;
    this.mediaSource.endOfStream();
  }

  private cleanup(): void {
    if (this.sourceBuffer) {
      this.sourceBuffer.onupdateend = null;
      this.mediaSource.removeSourceBuffer(this.sourceBuffer);
      this.sourceBuffer = null;
    }

    this.mediaSource.onsourceopen = null;

    this.audio.src = '';
    this.audio.onended = null;
    this.audio.onplay = null;
    this.audio.remove();
  }

  public error(error: Error): void {
    this.handleError(error);
    this.close();
  }

  private handleError(error: Error): void {
    this.sentryUtil.trackError(error, { errorType: ErrorTypeEnum.AUDIO_MEDIA_SOURCE_ERROR });
  }
}
